import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ProcessingButton from '../../ProcessingButton';
import styles from '../console.module.scss';

import { FieldNumber } from '@/components/ui/kit/Field';
import ProgressBar from '@/components/ui/ProgressBar';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useTelemetryData from '@/hooks/useTelemetryData';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Console = ({ robot }) => {
  const robotParams = useSelector((state) => state.robotParams[robot.id]);
  const [params, setParams] = useState({
    FS_THR_ENABLE: null,
    FS_THR_VALUE: null,
  });

  const { getAddress } = useTelemetryData();
  const paramCommand = useParamCommand();

  // 수신된 파라미터 갯수
  const isLoadedCount = useMemo(() => {
    return Object.values(params).filter((value) => value !== null).length;
  }, [params]);

  // 파라미터 수신 중 여부
  const isLoading = useMemo(() => {
    return isLoadedCount < Object.keys(params).length;
  }, [isLoadedCount]);

  useMountEffect(() => {
    const address = getAddress(robot.id);
    const paramsGroup = Object.keys(params).map((key) => [...address, key, -1]);

    paramCommand.read(robot, paramsGroup);
  });

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (!Object.keys(params).includes(lastParamId)) return;

    setParams((prev) => ({
      ...prev,
      [lastParamId]: {
        value: robotParams.params[lastParamId].value,
        type: robotParams.params[lastParamId].type,
      },
    }));
  }, [robotParams]);

  const handleChange = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = e.target.value;

    setParams(nextParams);
  };

  const handleBlur = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = String(Number(e.target.value));

    setParams(nextParams);
  };

  const doSave = async (prefix) => {
    const paramSet = Object.entries(params)
      .filter(([key]) => key.startsWith(prefix))
      .map(([id, { value, type }]) => ({ id, value, type }));

    for (const param of paramSet) {
      try {
        await executeMavLinkCommand(robot, paramCommand.set(param.id, Number(param.value), param.type));
      } catch (error) {
        return false;
      }
    }
    return true;
  };

  if (isLoading) {
    return (
      <div className={cx('loading')}>
        <ProgressBar current={isLoadedCount} total={Object.keys(params).length} />
      </div>
    );
  }

  return (
    <div className={cx('container')}>
      <div className={cx('rows')}>
        <div className={cx('row')}>
          <div className={cx('label')}>Throttle FailSafe</div>
          <div className={cx('radioGroup')}>
            <label>
              <input
                type="radio"
                name="FS_THR_ENABLE"
                value={0}
                defaultChecked={params['FS_THR_ENABLE'].value === 0}
                onChange={handleChange}
              />
              Disabled
            </label>
            <label>
              <input
                type="radio"
                name="FS_THR_ENABLE"
                value={3}
                defaultChecked={params['FS_THR_ENABLE'].value === 3}
                onChange={handleChange}
              />
              Land
            </label>
            <label>
              <input
                type="radio"
                name="FS_THR_ENABLE"
                value={1}
                defaultChecked={params['FS_THR_ENABLE'].value === 1}
                onChange={handleChange}
              />
              RTL
            </label>
          </div>
        </div>
        <div className={cx(['row', 'center'])}>
          <div className={cx('label')}>PWM Threshold</div>
          <div className={cx('fieldWrapper')}>
            <FieldNumber
              name="FS_THR_VALUE"
              value={params['FS_THR_VALUE'].value}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
        </div>
      </div>
      <ProcessingButton text="Save" doProcess={() => doSave('FS_THR')} />
    </div>
  );
};

export default Console;
